import React from 'react';
import bindDeviceActions from '../device/redux/bindDeviceActions';
import { getLabelForDevice, getConditionLabel } from '../../common/ambient';
import { useDevice } from '../device/redux/hooks';
import { FormattedDataPoint, DeviceName } from '../device/';
import classNames from 'classnames';
import './AlertDescription.less';
import {
  getAlertParamsForDevice,
  TIME_ALERT_PARAM,
  getLeakDisplayValue,
  getBatteryDisplayValue,
  isLeakParam,
  isBatteryParam,
} from '.'
import { formatInterval, formatNextDate } from './timeAlertUtils';
import { ambient } from '@aw_products/ambient-weather-common';
// import PropTypes from 'prop-types';

const AlertDescription = bindDeviceActions(({ device, actions, alrt, showNextDate = false }) => {
  const theDevice = useDevice(device, actions, alrt.deviceId);
  let add = '';
  let iconClass = 'glyphicon glyphicon-warning-sign';
  let extraIcon = null;

  if (alrt.param === ambient.HST.ALERT_ID) {
    // WBGT Guidelines
    add = (
      <span>
        WBGT Guideline - {alrt.data.guideline.name + ' ' + alrt.data.zone}
      </span>
    );
  } else if (alrt.param === TIME_ALERT_PARAM) {
    // Handle time-based alerts
    iconClass = 'glyphicon glyphicon-time';
    const data = alrt.data || {};

    if (showNextDate) {
      // Full display with next date (for AlertList)
      const now = new Date();
      const nowET = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
      const todayET = new Date(nowET.getFullYear(), nowET.getMonth(), nowET.getDate());
      const tomorrowET = new Date(todayET);
      tomorrowET.setDate(tomorrowET.getDate() + 1);
      const isPastNoonET = nowET.getHours() >= 12;

      const nextDateObj = data.nextDate ? new Date(data.nextDate) : null;
      const nextDateOnly = nextDateObj ? new Date(nextDateObj.getFullYear(), nextDateObj.getMonth(), nextDateObj.getDate()) : null;

      let nextDateDisplay = null;
      if (nextDateObj) {
        if (nextDateOnly && nextDateOnly.getTime() === todayET.getTime()) {
          nextDateDisplay = isPastNoonET ? 'tomorrow' : 'today';
        } else if (nextDateOnly && nextDateOnly.getTime() === tomorrowET.getTime()) {
          nextDateDisplay = 'tomorrow';
        } else {
          nextDateDisplay = nextDateObj.toLocaleDateString();
        }
      }

      const isNextDatePast = nextDateObj && nextDateObj < now && nextDateOnly.getTime() !== todayET.getTime();

      add = (
        <span>
          {data.name || 'Time-based Alert'} - {data.message || 'No message'}
          {nextDateDisplay
            ? data.recurring
              ? ` (every ${data.everyXDays} days, next: ${nextDateDisplay})`
              : isNextDatePast
                ? ` (was due: ${nextDateDisplay})`
                : ` (due: ${nextDateDisplay})`
            : ` (no next date calculated)`
          }
        </span>
      );
    } else {
      // Simple display without next date
      add = <span className='time-alert'>
        {data.name || 'Time Alert'}
        {data.recurring && data.everyXDays && (
          <span> (Every {formatInterval(data.everyXDays)})</span>
        )}
      </span>;
    }
  } else if (alrt.param === 'notReporting') {
    add = <span>{getLabelForDevice(alrt.param, theDevice)}</span>;
  } else if (isLeakParam(alrt.param)) {
    // Leak alerts
    const leakStatus = getLeakDisplayValue(alrt.condition, alrt.threshold);
    add = (
      <span>
        {getLabelForDevice(alrt.param, theDevice)}
        {leakStatus === 'LEAK' ? ' Leak Detected' : leakStatus === 'OFFLINE' ? ' Leak Sensor Offline' : ' OK'}
      </span>
    );
    extraIcon = <i className='leak' />;
  } else if (isBatteryParam(alrt.param)) {
    // Battery alerts
    const batteryStatus = getBatteryDisplayValue(alrt.param, alrt.condition, alrt.threshold, theDevice);
    add = (
      <span>
        {getLabelForDevice(alrt.param, theDevice)} {batteryStatus}
      </span>
    );
  } else {
    // Standard alerts
    add = <span>
      {getLabelForDevice(alrt.param, theDevice) + ' ' + getConditionLabel(alrt.condition) + ' '}
      <FormattedDataPoint type={alrt.param} value={alrt.threshold} />
    </span>;
  }

  // IFTTT icon
  const iftttIcon = alrt.ifttt ? (
    <a
      href='https://ifttt.com/my_applets'
      rel='noopener noreferrer'
      target='_blank'
      title='This alert was created on IFTTT. You can modify it there'
      className='ifttt'
    />
  ) : null;

  const isWarning = theDevice && getAlertParamsForDevice(theDevice).indexOf(alrt.param) === -1;
  const warningTitle = isWarning ? 'Warning: This parameter is not currently available on this device. The alert will not trigger until the device reports this data.' : '';

  return (
    <div className={classNames('alert-description', { warn: isWarning, 'ifttt-alert': alrt.ifttt }, alrt.param)}>
      {iftttIcon}
      <i className={iconClass} title={warningTitle} />
      {extraIcon}
      {add}
    </div>
  );
});
export default AlertDescription;

AlertDescription.propTypes = {};
AlertDescription.defaultProps = {};

AlertDescription.displayName = 'AlertDescription'
